/**
 * Adapter - Conversion between Widget V2 internal format and BackendWidget
 * 
 * These functions handle conversion between:
 * - Widget (V2 internal format with config/layout structure)
 * - BackendWidget (API format with flat fields)
 * - SimpleGridItem (layout system format)
 */

import type {
  Widget,
  WidgetConfig,
  WidgetLayout,
  DataConfig,
  VisualConfig,
  BucketDataConfig,
  CategoryDataConfig,
  GlobalMetricDataConfig,
  ChartVisualConfig,
  KpiVisualConfig,
  TextVisualConfig,
  DocumentsVisualConfig,
  GraphVisualConfig,
  MapVisualConfig,
  WidgetType,
  BackendWidget,
} from "@/models/widget-v2";
import {
  WIDGET_TYPES,
  fromBackendWidgetType,
  toBackendWidgetType,
  createDefaultWidgetConfig,
  getBackendWidgetType,
} from "@/models/widget-v2";
import type { SimpleGridItem } from "@/hooks/use-simple-layout";

// ============================================================================
// BackendWidget -> Widget V2 Conversion
// ============================================================================

/**
 * Converts a BackendWidget to Widget V2 internal format.
 * Used when loading widgets from API for internal processing.
 */
export function backendWidgetToWidgetV2(backendWidget: BackendWidget): Widget {
  const widgetType = fromBackendWidgetType(getBackendWidgetType(backendWidget, "line_widget"));
  
  // Create default config
  const defaultConfig = createDefaultWidgetConfig(widgetType, backendWidget.id || `widget-${Date.now()}`);
  
  // Extract data config from backend fields
  const dataConfig = extractDataConfigFromBackend(backendWidget, widgetType);
  
  // Extract visual config
  const visualConfig = extractVisualConfigFromBackend(backendWidget, widgetType);
  
  const config: WidgetConfig = {
    id: backendWidget.id || defaultConfig.id,
    type: widgetType,
    name: backendWidget.name || backendWidget.metadata?.name || defaultConfig.name,
    data: dataConfig || defaultConfig.data,
    visual: visualConfig || defaultConfig.visual,
    // Copier la configuration d'apparence si présente
    appearance: backendWidget.appearance,
  };
  
  const layout: WidgetLayout = {
    x: backendWidget.x ?? 0,
    y: backendWidget.y ?? 0,
    w: backendWidget.w ?? 2,
    h: backendWidget.h ?? 2,
  };
  
  return {
    id: backendWidget.id || defaultConfig.id,
    config,
    layout,
  };
}

/**
 * Extracts DataConfig from BackendWidget fields
 */
function extractDataConfigFromBackend(widget: BackendWidget, widgetType: WidgetType): DataConfig | null {
  // BucketMetricWidget (line, bar, area, table, heatmap, treemap, scatter, graph, map)
  if (widget.bucket_field || widget.bucket_type || widget.metrics) {
    return {
      kind: "bucket",
      bucketField: widget.bucket_field || "date$date",
      bucketType: widget.bucket_type || "date_histogram",
      metrics: (widget.metrics || [{ name: "count", type: "count", field: null }]).map(m => ({
        name: m.name || "count",
        type: m.type as BucketDataConfig["metrics"][0]["type"] || "count",
        field: m.field || null,
      })),
      size: widget.size ?? 10,
      dateInterval: (widget.date_interval as BucketDataConfig["dateInterval"]) || "month",
      numericInterval: widget.numeric_interval,
      // Split by for multiple series
      splitBy: widget.split_by || undefined,
      splitBySize: widget.split_by_size,
    } as BucketDataConfig;
  }
  
  // CategoryMetricWidget (pie, word_cloud)
  if (widget.field && !widget.bucket_field) {
    return {
      kind: "category",
      field: widget.field || "text$type",
      metric: widget.metric ? {
        name: widget.metric.name || "count",
        type: widget.metric.type as CategoryDataConfig["metric"]["type"] || "count",
        field: widget.metric.field || null,
      } : { name: "count", type: "count", field: null },
      size: widget.size ?? 10,
      minDocCount: widget.min_doc_count ?? 1,
      showMissing: widget.show_missing ?? false,
      missingLabel: widget.missing_label || "Missing",
    } as CategoryDataConfig;
  }
  
  // GlobalMetricWidget (kpi, gauge)
  if (widget.metric && !widget.field && !widget.bucket_field) {
    const dataConfig: GlobalMetricDataConfig = {
      kind: "global",
      metric: {
        name: widget.metric.name || "count",
        type: widget.metric.type as GlobalMetricDataConfig["metric"]["type"] || "count",
        field: widget.metric.field || null,
      },
    };
    if (widget.comparison) {
      dataConfig.comparison = {
        period: widget.comparison.period || "month",
        dateField: widget.date_field || "date$date",
      };
    }
    return dataConfig;
  }
  
  // Documents/Graph (documents_widget, graph_widget)
  const docTypes: WidgetType[] = [WIDGET_TYPES.DOCUMENTS, WIDGET_TYPES.GRAPH];
  if (docTypes.includes(widgetType)) {
    return {
      kind: "documents",
      size: widget.size ?? 20,
      sort: widget.sort ? {
        field: Object.keys(widget.sort[0])[0],
        order: "desc" as const,
      } : undefined,
      fields: widget.fields,
    };
  }
  
  // Text (text_widget)
  if (widgetType === WIDGET_TYPES.TEXT) {
    return { kind: "static" };
  }
  
  return null;
}

/**
 * Extracts VisualConfig from BackendWidget.
 * Prioritizes stored visual config from metadata.visual (persisted from previous edits),
 * falling back to sensible defaults per widget type.
 */
function extractVisualConfigFromBackend(widget: BackendWidget, widgetType: WidgetType): VisualConfig | null {
  const name = widget.name || widget.metadata?.name || "Widget";
  
  // If visual config was previously persisted in metadata, restore it
  const storedVisual = widget.metadata?.visual as Record<string, unknown> | undefined;
  if (storedVisual && Object.keys(storedVisual).length > 0) {
    const restored = { ...storedVisual, title: (storedVisual.title as string) || name } as VisualConfig;
    if (widgetType === WIDGET_TYPES.TEXT) {
      (restored as TextVisualConfig).blocks = (widget as Record<string, unknown>).blocks as unknown[];
    }
    return restored;
  }
  
  // Type-specific visual configs (defaults for new widgets)
  if (widgetType === WIDGET_TYPES.TEXT) {
    return {
      title: name,
      transparentBackground: true,
      padding: 8,
      blocks: (widget as Record<string, unknown>).blocks as unknown[],
    } as TextVisualConfig;
  }
  
  if (widgetType === WIDGET_TYPES.DOCUMENTS) {
    return {
      title: name,
      defaultView: "card",
    } as DocumentsVisualConfig;
  }
  
  if (widgetType === WIDGET_TYPES.GRAPH) {
    return {
      title: name,
      showLegend: true,
      nodeSize: 15,
      linkDistance: 30,
      chargeStrength: -200,
      showLabels: true,
      showTooltips: true,
    } as GraphVisualConfig;
  }
  
  if (widgetType === WIDGET_TYPES.MAP) {
    return {
      title: name,
      showLegend: true,
      projection: "mercator",
      defaultZoom: 1,
      showMarkerLabels: true,
    } as MapVisualConfig;
  }
  
  const kpiGaugeTypes: WidgetType[] = [WIDGET_TYPES.KPI, WIDGET_TYPES.GAUGE];
  if (kpiGaugeTypes.includes(widgetType)) {
    return {
      title: name,
      valueSize: "medium",
      showTrend: true,
      showComparison: true,
      format: { type: "number", precision: 0 },
    } as KpiVisualConfig;
  }
  
  // Default chart visual config
  return {
    title: name,
    subtitle: widget.description || undefined,
    showLegend: true,
    legendPosition: "top",
    showGrid: true,
    showDataLabels: false,
    animation: true,
    tooltips: true,
  } as ChartVisualConfig;
}

// ============================================================================
// Widget V2 -> BackendWidget Conversion
// ============================================================================

/**
 * Converts Widget V2 to BackendWidget format for API calls.
 */
export function widgetV2ToBackendWidget(widget: Widget): BackendWidget {
  const { config, layout } = widget;
  const chartType = toBackendWidgetType(config.type);
  
  const backendWidget: BackendWidget = {
    id: widget.id,
    // IMPORTANT: doc_type is required for backend to dispatch to correct widget subclass
    doc_type: chartType,
    chart: chartType as BackendWidget["chart"],
    type: chartType,
    name: config.name,
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
  };
  
  // Add data config fields based on kind
  const dataConfig = config.data;
  
  switch (dataConfig.kind) {
    case "bucket":
      backendWidget.bucket_field = dataConfig.bucketField;
      backendWidget.bucket_type = dataConfig.bucketType;
      backendWidget.metrics = dataConfig.metrics;
      backendWidget.size = dataConfig.size;
      backendWidget.date_interval = dataConfig.dateInterval;
      backendWidget.numeric_interval = dataConfig.numericInterval;
      // Split by for multiple series
      if (dataConfig.splitBy) backendWidget.split_by = dataConfig.splitBy;
      if (dataConfig.splitBySize) backendWidget.split_by_size = dataConfig.splitBySize;
      break;
      
    case "category":
      backendWidget.field = dataConfig.field;
      backendWidget.metric = dataConfig.metric;
      backendWidget.size = dataConfig.size;
      backendWidget.min_doc_count = dataConfig.minDocCount;
      backendWidget.show_missing = dataConfig.showMissing;
      backendWidget.missing_label = dataConfig.missingLabel;
      break;
      
    case "global":
      backendWidget.metric = dataConfig.metric;
      if (dataConfig.comparison) {
        backendWidget.comparison = {
          period: dataConfig.comparison.period,
          metric: dataConfig.metric,
        };
        backendWidget.date_field = dataConfig.comparison.dateField;
      }
      break;
      
    case "documents":
      backendWidget.size = dataConfig.size;
      if (dataConfig.sort) {
        backendWidget.sort = [{ [dataConfig.sort.field]: { order: dataConfig.sort.order } }];
      }
      backendWidget.fields = dataConfig.fields;
      break;
  }
  
  // Text widget: persist blocks as top-level field
  if (config.type === WIDGET_TYPES.TEXT) {
    const textVisual = config.visual as TextVisualConfig;
    if (textVisual.blocks) {
      backendWidget.blocks = textVisual.blocks;
    }
  }

  if (config.appearance) {
    backendWidget.appearance = config.appearance;
  }

  // Persist visual config in metadata so it survives round-trips
  backendWidget.metadata = {
    ...backendWidget.metadata,
    name: config.name,
    visual: config.visual as Record<string, unknown>,
  };
  
  return backendWidget;
}

// ============================================================================
// Widget V2 -> SimpleGridItem Conversion
// ============================================================================

/**
 * Converts Widget V2 to SimpleGridItem for layout system.
 * The widget data is stored as BackendWidget format.
 */
export function widgetV2ToGridItem(widget: Widget): SimpleGridItem {
  const backendWidget = widgetV2ToBackendWidget(widget);
  
  return {
    i: widget.id,
    x: widget.layout.x,
    y: widget.layout.y,
    w: widget.layout.w,
    h: widget.layout.h,
    widget: backendWidget,
  };
}

/**
 * Converts SimpleGridItem to Widget V2.
 */
export function gridItemToWidgetV2(gridItem: SimpleGridItem): Widget {
  const backendWidget = gridItem.widget;
  if (!backendWidget) {
    // Return a default widget if no widget data
    const defaultConfig = createDefaultWidgetConfig(WIDGET_TYPES.LINE, gridItem.i);
    return {
      id: gridItem.i,
      config: defaultConfig,
      layout: {
        x: gridItem.x,
        y: gridItem.y,
        w: gridItem.w,
        h: gridItem.h,
      },
    };
  }
  
  return backendWidgetToWidgetV2({
    ...backendWidget,
    x: gridItem.x,
    y: gridItem.y,
    w: gridItem.w,
    h: gridItem.h,
  });
}
